import BlockApi from '@/api/api';
import MultipleInputsDialog from '@/components/common/dialogs/MultipleInputsDialog';
import SingleInputDialog from '@/components/common/dialogs/SingleInputDialog';
import SearchField from '@/components/common/SearchField';
import FolderTreeView from '@/components/navigation/folder/FolderTreeView';
import ApplicationDetails from '@/components/navigation/wizard/application/ApplicationDetails';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { BackendApplication, RemovedBackendApplication } from '@/types/backend';
import { Folder } from '@/types/folder';
import { applicationCustomTooltip, applicationSearchOptions, sortBy } from '@/util/application';
import { filterApplicationsInFolders, findApplicationPath, mergeFoldersAndApplications } from '@/util/folder';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocalStorage } from 'usehooks-ts';
import CopyApplicationDialog from './applicationDialogs/CopyApplicationDialog';
import DeletedApplicationsDialog from './applicationDialogs/DeletedApplicationsDialog';
import MoveApplicationDialog from './applicationDialogs/MoveApplicationDialog';
import CreateFolderDialog from './folderDialogs/CreateFolderDialog';
import MoveFolderDialog from './folderDialogs/MoveFolderDialog';

type ApplicationManagementDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ApplicationManagementDialog: FC<ApplicationManagementDialogProps> = (props) => {
  const { config } = useConfig();
  const { confirm, showError } = useDialogs();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Folder[]>();
  const [filteredData, setFilteredData] = useState<Folder[]>();
  const [selectedApplication, setSelectedApplication] = useState<BackendApplication>();
  const [selectedFolder, setSelectedFolder] = useState<Folder>();
  const [deletedApplicationsList, setDeletedApplicationsList] = useState<RemovedBackendApplication[]>([]);
  const [openCopyApplicationDialog, setOpenCopyApplicationDialog] = useState(false);
  const [openMoveApplicationDialog, setOpenMoveApplicationDialog] = useState(false);
  const [openRenameApplicationDialog, setOpenRenameApplicationDialog] = useState(false);
  const [openRenameFolderDialog, setOpenRenameFolderDialog] = useState(false);
  const [openDeletedApplicationsDialog, setOpenDeletedApplicationsDialog] = useState(false);
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [openMoveFolderDialog, setOpenMoveFolderDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAppsBy, setSortAppsBy] = useLocalStorage('appManSort', 'label');
  const [invert, setInvert] = useState(false);

  const getData = async () => {
    if (!auth.user?.access_token) return;
    const folders = await BlockApi(config.apiUrl, auth.user.access_token).folder.list();
    const applications = await BlockApi(config.apiUrl, auth.user.access_token).application.list();

    if (folders && applications) {
      setData(mergeFoldersAndApplications(sortBy('name', folders), sortBy('label', applications)));
    }

    setLoading(false);
  };

  const handleFolderClick = (folder: Folder) => {
    setSelectedFolder(folder);
    setSelectedApplication(undefined);
  };

  const handleTreeViewClick = (application: BackendApplication) => {
    setSelectedFolder(undefined);
    setSelectedApplication(application);
  };

  const handleCopyApplication = (folderId: number, revision?: number) => {
    if (!(auth.user && auth.user.access_token) || !selectedApplication?.applicationId || !folderId) return;
    setOpenCopyApplicationDialog(false);
    setLoading(true);
    BlockApi(config.apiUrl, auth.user.access_token)
      .application.copy(selectedApplication?.applicationId, folderId, revision)
      .then(
        () => {
          getData();
        },
        (error) => {
          errorHandling(error);
        }
      );
  };

  const handleMove = (newParentFolderId: number | undefined) => {
    if (!auth.user?.access_token) return;

    setLoading(true);
    if (selectedApplication?.applicationId && newParentFolderId) {
      setOpenMoveApplicationDialog(false);
      BlockApi(config.apiUrl, auth.user.access_token)
        .application.move(selectedApplication?.applicationId, newParentFolderId)
        .then(
          () => {
            getData();
          },
          (error) => {
            errorHandling(error);
          }
        );
    } else if (selectedFolder?.folderId) {
      setOpenMoveFolderDialog(false);
      BlockApi(config.apiUrl, auth.user.access_token)
        .folder.move(selectedFolder.folderId, newParentFolderId)
        .then(
          () => {
            getData();
          },
          (error) => {
            errorHandling(error);
          }
        );
    }
  };

  const handleRename = (newLabel?: string, newName?: string) => {
    if (!auth.user?.access_token) return;

    setLoading(true);
    if (selectedApplication && selectedApplication?.applicationId) {
      setOpenRenameApplicationDialog(false);
      BlockApi(config.apiUrl, auth.user.access_token)
        .application.rename(selectedApplication?.applicationId, newName, newLabel)
        .then(
          (result) => {
            if (result.ok && result.status === 200) {
              getData();
            } else {
              errorHandling(result);
            }
          },
          (error) => {
            errorHandling(error);
          }
        );
    } else if (selectedFolder && selectedFolder?.folderId && newName) {
      setOpenRenameFolderDialog(false);
      BlockApi(config.apiUrl, auth.user.access_token)
        .folder.rename(selectedFolder?.folderId, newName)
        .then(
          () => {
            getData();
          },
          (error) => {
            errorHandling(error);
          }
        );
    }
  };

  const handleRemove = async () => {
    if (!auth.user?.access_token) return;

    if (selectedApplication?.applicationId) {
      confirm({
        title: `Deleting application: ${selectedApplication.name}`,
        description: `Are you sure you want to remove the following application: ${selectedApplication.name}?`,
        confirmationText: 'Remove',
        cancellationButtonProps: { color: 'secondary' }
      }).then((result) => {
        if (!result.confirmed) return;

        if (selectedApplication.applicationId && auth.user?.access_token) {
          BlockApi(config.apiUrl, auth.user.access_token)
            .application.remove(selectedApplication.applicationId)
            .then(
              () => {
                getData();
              },
              (error) => {
                errorHandling(error);
              }
            );
        }
      });
    } else if (selectedFolder?.folderId) {
      confirm({
        title: `Deleting folder: ${selectedFolder.name}`,
        description: `Are you sure you want to remove the following folder: ${selectedFolder.name}?`,
        confirmationText: 'Remove',
        cancellationButtonProps: { color: 'secondary' }
      }).then((result) => {
        if (!result.confirmed || !auth.user?.access_token) return;

        BlockApi(config.apiUrl, auth.user.access_token)
          .folder.remove(selectedFolder.folderId)
          .then(
            () => {
              getData();
            },
            (error) => {
              errorHandling(error);
            }
          );
      });
    }
  };

  const handleCreateFolder = (folderName: string, parentFolderId: number | undefined) => {
    setOpenCreateFolderDialog(false);
    if (!auth.user?.access_token) return;

    BlockApi(config.apiUrl, auth.user.access_token)
      .folder.create(folderName, parentFolderId)
      .then(
        () => {
          getData();
        },
        (error) => {
          errorHandling(error);
        }
      );
  };

  const handleDeletedDialog = async () => {
    if (!auth.user?.access_token) return;

    BlockApi(config.apiUrl, auth.user.access_token)
      .application.listRemoved()
      .then(
        (result) => {
          if (result) {
            setDeletedApplicationsList(result);
            setOpenDeletedApplicationsDialog(true);
          }
        },
        (error) => {
          errorHandling(error);
        }
      );
  };

  const handleRestoreApplication = (applicationId: string) => {
    if (!auth.user?.access_token) return;

    setOpenDeletedApplicationsDialog(false);
    BlockApi(config.apiUrl, auth.user.access_token)
      .application.restore(applicationId)
      .then(
        () => {
          getData();
        },
        (error) => {
          errorHandling(error);
        }
      );
  };

  const errorHandling = (error: Error | Response) => {
    if (error instanceof Error) {
      showError({
        message: error.message,
        stacktrace: error
      });
    } else {
      showError({
        title: 'An error has occured',
        message: error.statusText,
        stacktrace: new Error(JSON.stringify(error))
      });
    }
  };

  useEffect(() => {
    if (props.open) {
      setLoading(true);
      getData();
    } else if (!props.open) {
      setSelectedApplication(undefined);
    }
  }, [props.open]);

  useEffect(() => {
    if (!data) return;
    setFilteredData(filterApplicationsInFolders(data, searchQuery, sortAppsBy, invert));
  }, [data, searchQuery, sortAppsBy, invert]);

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} maxWidth='md' fullWidth>
        <DialogTitle>Application Management</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between' }}>
            <Box flexGrow={2} paddingRight={2.5}>
              <Box mb={1}>
                <SearchField
                  sortBy={sortAppsBy}
                  invert={invert}
                  searchOptions={applicationSearchOptions}
                  onSort={(changeSortBy, reverse) => {
                    changeSortBy && setSortAppsBy(changeSortBy);
                    reverse && setInvert(!invert);
                  }}
                  searchQuery={searchQuery}
                  setSearchQuery={(query) => setSearchQuery(query)}
                  customTooltip={applicationCustomTooltip}
                  autoFocus={props.open}
                />
              </Box>
              {filteredData && filteredData.length > 0 ? (
                <FolderTreeView
                  folders={filteredData}
                  onFolderClick={(folder) => handleFolderClick(folder)}
                  onApplicationClick={(application) => handleTreeViewClick(application)}
                />
              ) : loading ? (
                <LinearProgress />
              ) : (
                <Typography height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                  No folders found
                </Typography>
              )}
            </Box>
            <Box
              display={'flex'}
              flexGrow={1}
              flexDirection={'column'}
              justifyContent={'space-between'}
              rowGap={7.5}
              mt={'53px'}
            >
              <Stack direction='column' spacing={2}>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={!selectedApplication && !selectedFolder}
                  onClick={handleRemove}
                >
                  Remove
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={!selectedApplication && !selectedFolder}
                  onClick={() =>
                    selectedApplication ? setOpenMoveApplicationDialog(true) : setOpenMoveFolderDialog(true)
                  }
                >
                  Move
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={!selectedApplication && !selectedFolder}
                  onClick={() =>
                    selectedApplication ? setOpenRenameApplicationDialog(true) : setOpenRenameFolderDialog(true)
                  }
                >
                  Rename
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={!selectedApplication}
                  onClick={() => setOpenCopyApplicationDialog(true)}
                >
                  Copy
                </Button>
              </Stack>
              <Stack direction='column' spacing={2}>
                <Button variant='contained' color='primary' onClick={() => setOpenCreateFolderDialog(true)}>
                  Create folder
                </Button>
              </Stack>
            </Box>
          </Box>
          <Box marginTop={1}>
            <ApplicationDetails
              application={selectedApplication}
              path={
                selectedApplication && selectedApplication.applicationId
                  ? findApplicationPath(data, selectedApplication.applicationId)
                  : undefined
              }
            />
            <Button variant='outlined' color='primary' onClick={() => handleDeletedDialog()}>
              View Removed
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <CopyApplicationDialog
        folders={data}
        open={openCopyApplicationDialog}
        currentRevision={selectedApplication?.revision}
        onSelect={(folderId: number, revision?: number) => handleCopyApplication(folderId, revision)}
        onClose={() => setOpenCopyApplicationDialog(false)}
      />
      <MoveApplicationDialog
        folders={data}
        open={openMoveApplicationDialog}
        onSelect={(folderId: number) => handleMove(folderId)}
        onClose={() => setOpenMoveApplicationDialog(false)}
      />
      <MoveFolderDialog
        folders={data}
        selectedFolderId={selectedFolder?.folderId}
        open={openMoveFolderDialog}
        onSelect={(folderId: number | undefined) => handleMove(folderId)}
        onClose={() => setOpenMoveFolderDialog(false)}
      />
      <CreateFolderDialog
        folders={data}
        open={openCreateFolderDialog}
        onSelect={(folderName: string, parentFolderId: number | undefined) =>
          handleCreateFolder(folderName, parentFolderId)
        }
        onClose={() => setOpenCreateFolderDialog(false)}
      />
      <DeletedApplicationsDialog
        open={openDeletedApplicationsDialog}
        deletedApplications={deletedApplicationsList}
        onSelect={(applicationId) => {
          handleRestoreApplication(applicationId);
        }}
        onClose={() => setOpenDeletedApplicationsDialog(false)}
      />
      <SingleInputDialog
        open={openRenameFolderDialog}
        title='Rename folder'
        label='New name'
        onClose={() => setOpenRenameFolderDialog(false)}
        onConfirm={(newName: string) => handleRename(newName)}
      />
      <MultipleInputsDialog
        open={openRenameApplicationDialog}
        title='Rename application'
        inputs={['Label', 'Name']}
        defaultValues={[selectedApplication?.label ?? '', selectedApplication?.name ?? '']}
        onClose={() => setOpenRenameApplicationDialog(false)}
        onConfirm={(newLabel: string, newName: string) => handleRename(newLabel, newName)}
      />
    </>
  );
};

export default ApplicationManagementDialog;
